import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { PersonaType, isSellerPersona } from '@/hooks/useUserPersona';
import { resolveLandingPath, companyHasInventory, deskSetupGraduated } from '@/lib/landing';
import SEO from '@/components/SEO';
import Footer from '@/components/Footer';
import AuthLoadingSkeleton from '@/components/AuthLoadingSkeleton';
import MarketingHeader from '@/components/home/MarketingHeader';
import Hero from '@/components/home/Hero';
import ProofBar from '@/components/home/ProofBar';
import QuietCost from '@/components/home/QuietCost';
import BothSides from '@/components/home/BothSides';
import HowItWorks from '@/components/home/HowItWorks';
import InterestDemo from '@/components/home/InterestDemo';
import ProductProof from '@/components/home/ProductProof';
import PrivacySection from '@/components/home/PrivacySection';
import ComparisonTable from '@/components/home/ComparisonTable';
import PersonaColumns from '@/components/home/PersonaColumns';
import ObjectionStrip from '@/components/home/ObjectionStrip';

import ClosingCTA from '@/components/home/ClosingCTA';

export const ROUTE_META = {
  path: '/',
  title: 'Cannafax homepage',
  description:
    'Public marketing homepage explaining what Cannafax does for buyers, sellers and power traders, with links to sign in and book a demo.',
  personas: ['seller', 'buyer_seller', 'buyer'] as const,
  concepts: [
    'Cannafax is a private network, not a marketplace — nothing is publicly listed',
    'Sellers see which listings earned buyer attention after a menu send',
    'Buyers see current availability and pricing and can ask in one tap',
    'Buyers can watch what they need and get instant alerts or a daily digest; sellers get Your Next Move™ each morning: the one buyer or listing worth acting on',
    'Buyer accounts are free with no trial clock; paid plans add seller and power trader capability',
  ],
};

const JSON_LD = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      name: 'Cannafax',
      url: 'https://cannafax.ai/',
      description:
        'A private network for bulk cannabis inventory. Menus go out, buyer interest comes back.',
    },
    {
      '@type': 'SoftwareApplication',
      name: 'Cannafax',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      url: 'https://cannafax.ai/',
      description:
        'Send bulk inventory menus and see which listings earned buyer attention. Private, invite-only, supplier identity never exposed.',
    },
  ],
};

/**
 * Public marketing homepage at `/`.
 *
 * Signed-in visitors keep the previous root behaviour: onboarding check, then
 * persona landing page. That redirect resolves before any marketing content
 * paints, so there is no flash of the landing page for existing users.
 */
const JSON_LD_ID = 'home-json-ld';

const Home = () => {
  const { user, loading, initializing } = useAuth();
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);

  // Structured data belongs in <head>, so inject it there and clean up on exit.
  useEffect(() => {
    let tag = document.getElementById(JSON_LD_ID);
    if (!tag) {
      tag = document.createElement('script');
      tag.id = JSON_LD_ID;
      tag.setAttribute('type', 'application/ld+json');
      document.head.appendChild(tag);
    }
    tag.textContent = JSON.stringify(JSON_LD);
    return () => {
      document.getElementById(JSON_LD_ID)?.remove();
    };
  }, []);

  // Clear any stale auth redirect lock when landing on the public root.
  useEffect(() => {
    sessionStorage.removeItem('auth_redirect_lock');
    sessionStorage.removeItem('auth_redirect_target');
  }, []);

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;

    const routeSignedInUser = async () => {
      if (!user || loading || initializing) return;
      setRedirecting(true);

      timeout = setTimeout(() => {
        console.warn('[Home] redirect timeout — falling back to the persona landing rule');
        navigate(resolveLandingPath(null), { replace: true });
      }, 10000);

      try {
        const { data: profile } = await supabase
          .from('users')
          .select('first_name, last_name, phone, company_id')
          .eq('id', user.id)
          .maybeSingle();

        const needsOnboarding =
          !profile?.first_name || !profile?.last_name || !profile?.phone || !profile?.company_id;

        if (needsOnboarding) {
          navigate('/onboarding', { replace: true });
          return;
        }

        const { data: personaData } = await supabase
          .from('companies')
          .select('persona')
          .eq('id', profile.company_id)
          .maybeSingle();

        const persona = (personaData?.persona ?? null) as PersonaType | null;
        // Sellers always land on Your Desk (Desk Setup guides a brand-new seller);
        // the inventory check is kept for callers that still pass it through.
        const hasInventory = isSellerPersona(persona)
          ? await companyHasInventory(profile.company_id)
          : null;

        const graduated = persona === 'buyer' ? await deskSetupGraduated() : null;
        navigate(resolveLandingPath(persona, hasInventory, graduated), { replace: true });
      } catch (err) {
        console.error('[Home] redirect failed, falling back', err);
        navigate(resolveLandingPath(null), { replace: true });
      } finally {
        if (timeout) clearTimeout(timeout);
      }
    };

    routeSignedInUser();
    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [user, loading, initializing, navigate]);

  // Arriving from another page via /#section: the sections only exist once the
  // auth check finishes, so the browser's own jump misses them. Scroll then.
  const showMarketing = !(initializing || loading || user || redirecting);
  useEffect(() => {
    if (!showMarketing) return;
    const id = decodeURIComponent(window.location.hash.slice(1));
    if (!id) return;
    const raf = requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ block: 'start' });
    });
    return () => cancelAnimationFrame(raf);
  }, [showMarketing]);

  // Signed-in (or still resolving) — never paint marketing content.
  if (initializing || loading || user || redirecting) {
    return <AuthLoadingSkeleton />;
  }

  return (
    <>
      <SEO
        title="Cannafax — your menu goes out, the interest comes back"
        description="A private network for bulk inventory. Sellers see which listings earned buyer attention; buyers see what's actually available, first. No marketplace, no public listings."
        canonical="https://cannafax.ai/"
        url="https://cannafax.ai/"
      />
      <div className="min-h-screen bg-background">
        <MarketingHeader />
        <main>
          <Hero />
          <ProofBar />
          <QuietCost />
          <BothSides />
          <HowItWorks />
          <InterestDemo />
          <ProductProof />
          <PrivacySection />
          <ComparisonTable />
          <PersonaColumns />

          <ObjectionStrip />
          <ClosingCTA />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Home;